/**
 * CSRF token plumbing for the shared axios instance.
 *
 * This is a deliberate port of `@spectora/base-api-service`'s `csrfToken.ts`
 * (PLT-2102), not an import: web-viewer does not depend on that package, and
 * pulling it in would drag axios 1.x semantics, its `processResponse` pipeline
 * and its unauthorized-check behavior into a Vue 2 app with its own
 * conventions. The contract is small; the coupling is not worth it. Keep the
 * two in step — PLT-2103.
 */

/**
 * The CSRF token header name, on both the response Rails sends it in and the
 * request the client sends it back on. This is the name Rails' own
 * `request.x_csrf_token` reads, so it is not ours to choose.
 */
export const CSRF_TOKEN_HEADER = 'X-CSRF-Token'

/**
 * The session's CSRF token, held in **module scope** — deliberately not on an
 * axios instance.
 *
 * Rails hands the token back on every response (an `after_action` on
 * `ApplicationController`); this module is where the client keeps it between
 * capturing it off a response and attaching it to the next write.
 *
 * Two reasons it lives here rather than in `service.defaults.headers.common`:
 *
 *  - `headers.common` is sent on *every* request, including `GET` and `HEAD`.
 *    A CSRF token has no business on a read.
 *  - `headers.common` is per-instance, and there is more than one instance:
 *    `src/api/index.js` forks one via `railsService.create({})`. Whichever
 *    instance saw the response would be the only one holding the token.
 *
 * Memory only. Putting it in `localStorage` or a cookie would make it readable
 * from every `*.spectora.com` subdomain — and customer-authored content is
 * served from `*.hosting.spectora.com` — which is the exposure this design
 * exists to avoid.
 *
 * The value is a fresh mask on each response, but every mask validates against
 * the same `session[:_csrf_token]`, so the stored value stays valid for the
 * life of the session — and a session rotation at login is picked up from the
 * very next response with no refresh logic anywhere.
 */
let csrfToken = null

/**
 * Stores the token captured from a response, replacing any previous value.
 * A blank or missing value is ignored rather than clearing the stored token:
 * a response that carries no header (a third-party host, a static asset) tells
 * us nothing about whether the session's token is still good.
 */
export const setCsrfToken = (value) => {
  if (typeof value !== 'string' || value === '') return

  csrfToken = value
}

/** The stored token, or `null` when no response has carried one yet. */
export const getCsrfToken = () => csrfToken

/**
 * Drops the stored token. Exists for tests — nothing in the app clears it,
 * because a rotation replaces it and a sign-out takes the page with it.
 */
export const clearCsrfToken = () => {
  csrfToken = null
}

/**
 * Reads the token off a set of response headers and stores it.
 *
 * Header names are case-insensitive on the wire and the shape differs by
 * transport. On axios 0.24 — which is what this app is on — `response.headers`
 * is a plain object with lower-cased keys, and there is no `AxiosHeaders` and
 * no case-insensitive `get`. Both spellings are checked anyway, plus a `get`
 * function if one is ever present, so a hand-rolled test double, a real
 * response, and a future axios upgrade all behave the same.
 *
 * A nullish `headers` is a no-op: a cancelled request has no response to read.
 */
export const captureCsrfToken = (headers) => {
  if (!headers) return

  const getter = headers.get

  if (typeof getter === 'function') {
    setCsrfToken(getter.call(headers, CSRF_TOKEN_HEADER))
  }

  setCsrfToken(headers[CSRF_TOKEN_HEADER])
  setCsrfToken(headers[CSRF_TOKEN_HEADER.toLowerCase()])
}

/** The HTTP methods that need a CSRF token. Reads deliberately carry none. */
const STATE_CHANGING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE']

/**
 * Whether a request method needs the token. axios lower-cases `config.method`
 * and leaves it unset when the caller relies on the default, so normalise both.
 */
export const isStateChangingMethod = (method) =>
  STATE_CHANGING_METHODS.includes((method || 'GET').toUpperCase())

/**
 * Whether a request is bound for our own host, and so may carry the token.
 *
 * Ported from `packages/spectora-base-api-service/src/index.ts:168` (PR #2703 /
 * PLT-1244). This is the second of two independent guards keeping the token off
 * third-party hosts — the first being that the interceptors are registered on
 * the shared instance only, which the cloud-function
 * (`src/store/modules/reports/actions.js`) and Hermes
 * (`src/services/hermesService.js`) call sites bypass by importing bare
 * `axios`. That structural guard disappears the day someone routes an external
 * call through `railsService`, so the check is worth having on its own.
 *
 * For that to hold, this has to agree with axios about which URLs are
 * absolute: any url axios sends cross-origin must come back external. axios
 * 0.24's `isAbsoluteURL` (`lib/helpers/isAbsoluteURL.js`) is
 * `/^([a-z][a-z\d+\-.]*:)?\/\//i` — the scheme is optional and the match is
 * case-insensitive — so `//evil.example.com/x` and `HTTPS://evil.example.com/x`
 * are both absolute to axios: `baseURL` is never prepended and the request
 * leaves for the foreign host. An exact-prefix check on `http://`/`https://`
 * classified both as internal. The same regex is used here, and the URL is
 * resolved against the page origin so a protocol-relative one parses to the
 * origin a browser would actually send it to instead of throwing.
 *
 * A parse failure is treated as internal. Only a value that passes the
 * absolute test yet is not a URL at all (`http://` on its own) gets there: it
 * can reach neither our host nor a foreign one, so the safe answer is the one
 * that changes nothing about where the request goes.
 */
export const isInternalRequest = (url, baseURL) => {
  if (!url) return true

  // Relative URLs are always internal: axios prepends baseURL to them. This is
  // axios 0.24's own isAbsoluteURL test, kept identical on purpose.
  if (!/^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) {
    return true
  }

  try {
    const requestOrigin = new URL(url, window.location.origin).origin

    if (baseURL) {
      return requestOrigin === new URL(baseURL, window.location.origin).origin
    }

    return requestOrigin === window.location.origin
  } catch (error) {
    console.warn('[csrfToken] Failed to parse URL:', error)
    return true
  }
}
